<template>
  <div v-if="launchDarkly.enable_selectors_dnd && selectedItemIds.length" class="flex items-center gap-2">
    <CButton
      class="pa-1 pl-2 !normal-case !tracking-normal underline"
      size="tiny"
      text
      @click="emit('clear-selection')"
    >
      {{ t('canvasUI.common.clear') }}
    </CButton>
    <div v-if="shouldShowAddButton">
      <CButton
        v-if="!addedItemIds.length"
        class="mx-2"
        size="small"
        @click="emit('items-added', selectedItemIds, 'bottom')"
      >
        {{ t('canvasUI.common.add') }} ({{ totalItemsToAdd }})
      </CButton>
      <template v-else>
        <div
          v-if="
            launchDarkly.selectors_dnd_batch_add_style &&
            ['buttons'].includes(launchDarkly.selectors_dnd_batch_add_style)
          "
          class="flex gap-2"
        >
          <CButton size="small" @click="emit('items-added', selectedItemIds, 'top')">
            {{ t('canvasUI.canvasBuilder.sectionSelectorToolbar.addTop') }}
          </CButton>
          <CButton size="small" @click="emit('items-added', selectedItemIds, 'bottom')">
            {{ t('canvasUI.canvasBuilder.sectionSelectorToolbar.addBottom') }}
          </CButton>
        </div>
        <CDropdown
          v-else-if="
            launchDarkly.selectors_dnd_batch_add_style &&
            ['dropdown'].includes(launchDarkly.selectors_dnd_batch_add_style)
          "
          :options="addOptions"
          trigger="click"
          @select="selectOption"
        >
          <CButton
            v-tooltip="t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.buttons.tooltips.actions')"
            size="small"
          >
            {{ t('canvasUI.common.add') }} ({{ totalItemsToAdd }})
          </CButton>
        </CDropdown>
        <CButton v-else class="mx-2" size="small" @click="emit('items-added', selectedItemIds, 'bottom')">
          {{ t('canvasUI.common.add') }} ({{ totalItemsToAdd }})
        </CButton>
      </template>
    </div>
    <CButton
      v-else
      class="mx-2 bg-error2"
      size="small"
      :style="{
        '--n-border': '1px solid transparent',
        '--n-border-disabled': '1px solid transparent',
        '--n-border-focus': '1px solid transparent',
        '--n-border-hover': '1px solid transparent',
        '--n-border-pressed': '1px solid transparent',
        '--n-text-color': themeVars.error,
        '--n-text-color-focus': themeVars.error2,
        '--n-text-color-hover': themeVars.error2,
        '--n-text-color-disabled': themeVars.error2,
        '--n-text-color-pressed': themeVars.error2,
      }"
      type="error"
      @click="emit('items-removed', selectedItemIds)"
    >
      {{ t('canvasUI.common.remove') }} ({{ totalSelected }})
    </CButton>
  </div>
</template>

<script setup lang="ts">
import { computed, h, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasRetrieve } from '@openapi'
import type { DropdownOption } from 'naive-ui'

const props = withDefaults(
  defineProps<{
    totalSelected: number
    addedItemIds?: CanvasRetrieve['id'][]
    selectedItemIds?: CanvasRetrieve['id'][]
  }>(),
  {
    addedItemIds: () => [],
    selectedItemIds: () => [],
  },
)

const emit = defineEmits<{
  (e: 'clear-selection'): void
  (e: 'items-added', itemIds: CanvasRetrieve['id'][], position?: 'top' | 'bottom'): void
  (e: 'items-removed', itemIds: CanvasRetrieve['id'][]): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const trackUsage = inject<((id: string, payload: Record<string, any>) => void) | undefined>('trackUsage')

const { t } = useI18n()
const themeVars = useThemeVars()

const shouldShowAddButton = computed(() => {
  const addedIds = new Set(props.addedItemIds)
  return !props.selectedItemIds.every((id) => addedIds.has(id))
})

const totalItemsToAdd = computed(() => {
  return props.selectedItemIds.filter((id) => !props.addedItemIds.includes(id)).length
})

const addOptions: DropdownOption[] = [
  {
    label: t('canvasUI.canvasBuilder.sectionSelectorToolbar.top'),
    key: 'top',
    icon: () => h(CIcon, { icon: 'up-to-line', faType: 'fas' }),
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelectorToolbar.bottom'),
    key: 'bottom',
    icon: () => h(CIcon, { icon: 'down-from-line', faType: 'fas' }),
  },
]

function selectOption(value: 'top' | 'bottom') {
  trackUsage?.('Section Selector Toolbar Add - Dropdown Variation', { position: value })
  emit('items-added', props.selectedItemIds, value)
}
</script>
